import type { MapWellPaint } from 'components/map/atoms';
import type { SectionColorRule } from './attribute-colors';
import type { WellFilter } from './well-filters';

/**
 * Turning the rail's Wells rule into paint for the MAP'S OWN well layers, so the rule
 * controls the wells the map already draws rather than a layer laid over them. UAT-07:
 * "control the existing well objects on the map not an added layer on top of it".
 */

/**
 * Which rule attributes the `wells-v2` tiles can answer for every well, and the tile
 * property that answers them.
 *
 * Only `status`. The tiles carry `has_survey`, `hole_direction`, `lease_name`, `status`,
 * `type`, `uwi` and `well_number` — no operator and no landing zone — so the map cannot
 * know those for a well the section did not cross. That is why an overlay still exists
 * for them: the cut is the only source, and it covers the section's crossings alone.
 * Adding the two properties to the tiles is what would let this table grow.
 */
const TILE_PROPERTY_BY_ATTRIBUTE: Record<string, string> = {
  status: 'status',
};

/**
 * Whether a rule can paint every well on the map rather than only the section's.
 *
 * @param {SectionColorRule | undefined} rule - A rail Wells rule.
 * @return {boolean} True when the tiles can answer the rule's attribute.
 */
export const paintsEveryWell = (rule: SectionColorRule | undefined): boolean =>
  rule !== undefined &&
  rule.enabled &&
  rule.applyTo?.map !== false &&
  rule.selectedValues.length > 0 &&
  Object.keys(rule.valueColors).length > 0 &&
  TILE_PROPERTY_BY_ATTRIBUTE[rule.attribute] !== undefined;

/**
 * The map's well recolouring for a set of rail rules, or null to leave the map's own
 * colours alone.
 *
 * Only values the rule both selected and coloured are handed over; a status it says
 * nothing about keeps the map's own colour rather than going blank.
 *
 * @param {SectionColorRule[]} rules - The rail's Wells rules.
 * @return {MapWellPaint | null} What the map should paint, or null for no override.
 */
export const mapWellPaintFor = (
  rules: SectionColorRule[]
): MapWellPaint | null => {
  const rule = rules.find((candidate) => paintsEveryWell(candidate));
  if (!rule) {
    return null;
  }
  const colors: Record<string, string> = {};
  rule.selectedValues.forEach((value) => {
    const color = rule.valueColors[value];
    if (color !== undefined) {
      colors[value] = color;
    }
  });
  return Object.keys(colors).length === 0
    ? null
    : { property: TILE_PROPERTY_BY_ATTRIBUTE[rule.attribute], colors };
};

/**
 * The statuses the rail's filters leave standing, for the map's own wells — or null
 * where the filters say nothing the tiles can act on.
 *
 * Status only, for the same reason the colouring is: the tiles carry no operator and no
 * landing zone, so the map cannot tell whether a well it drew passes a filter written
 * over either. Those stay section-scoped rather than half-applied, which would hide
 * wells on a rule the map cannot actually evaluate.
 *
 * @param {WellFilter[]} filters - The rail's Wells filters.
 * @return {string[] | null} The statuses to keep, or null for no narrowing.
 */
export const mapWellStatusFilterFor = (
  filters: WellFilter[]
): string[] | null => {
  const status = filters.find(
    (filter) =>
      filter.enabled &&
      filter.attribute === 'status' &&
      filter.values.length > 0
  );
  return status ? status.values : null;
};
